import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { BadgeCheck, Loader2, Pencil, Plus, RotateCcw, Trash2, Upload } from 'lucide-react';
import { Drawer } from '@components/Drawer';
import { Button } from '@components/Button';
import { ConfirmDialog, Modal } from '@components/Dialog';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import creditCardService from '@services/creditCardService';
import {
  CARD_BRAND_LABELS,
  CARD_TRANSACTION_KIND_LABELS,
  isPaymentGeneratedItem,
  type Account,
  type CardTransaction,
  type Category,
  type CreditCard,
  type CreditCardInvoice,
  type DeleteScope,
} from '@/types/finance';
import { pad2 } from '@components/dateInputUtils';
import { addCompetence, cardCompetence, competenceFromYearMonth, competenceToKey, currentCompetence, type Competence } from '@/utils/competence';
import { formatDateBR, todayISO } from '@/utils/formatters';
import { formatBRL } from '@/utils/money';
import { shiftMonth } from '@/utils/period';
import { AmountText, BalanceText, ColorChip, CompetencePicker, InvoiceStatusBadge } from './financeBits';
import { CardFormModal } from './CardFormModal';
import { CardTransactionFormModal } from './CardTransactionFormModal';
import { PayInvoiceModal } from './PayInvoiceModal';

interface Props {
  cardId: number;
  /** Fatura a selecionar ao abrir (ex.: link do painel Hoje); precisa estar no intervalo. */
  initialInvoiceId?: number | null;
  /** Contas ativas (formulário do cartão). */
  accounts: Account[];
  categories: Category[];
  onClose: () => void;
  /** Avisa a lista para recarregar (totais mudaram, cartão editado). */
  onChanged: () => void;
  onError: (err: unknown) => void;
}

const Stat: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="min-w-0" title={hint}>
    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className="text-sm font-medium">{children}</p>
  </div>
);

/**
 * Data pré-preenchida para um item novo: hoje, se hoje já cai nesta fatura; senão o 1º dia do
 * mês da fatura (ou do mês anterior, quando o fechamento é dia 1) — uma data que cai nela.
 */
const dateInsideInvoice = (closingDay: number, inv: CreditCardInvoice): string => {
  const today = todayISO();
  const target = competenceFromYearMonth({ year: inv.year, month: inv.month });
  if (cardCompetence(closingDay, today) === target) return today;
  const ym = closingDay > 1 ? { year: inv.year, month: inv.month } : shiftMonth({ year: inv.year, month: inv.month }, -1);
  return `${ym.year}-${pad2(ym.month)}-01`;
};

const KIND_BADGE: Record<string, string> = { carryover: 'badge-warning', interest: 'badge-danger', refund: 'badge-success', fee: 'badge-neutral' };

/** Etiquetas de um item: parcela i/N, tipo (quando não é compra) e "Recorrente". */
const itemBadges = (t: CardTransaction) => (
  <>
    {t.repeat_total && t.repeat_total > 1 ? (
      <span className="badge-neutral ml-1">
        {t.repeat_index}/{t.repeat_total}
      </span>
    ) : null}
    {t.kind !== 'purchase' && <span className={clsx(KIND_BADGE[t.kind] ?? 'badge-neutral', 'ml-1')}>{CARD_TRANSACTION_KIND_LABELS[t.kind]}</span>}
    {t.recurring_transaction_id ? (
      <span className="badge-info ml-1" title="Gerado por uma recorrência (ao editar, deixa de ser atualizado por ela)">
        Recorrente
      </span>
    ) : null}
  </>
);

/**
 * Painel do cartão (`?open=<id>`): limite/em aberto, faturas do intervalo (GET /credit-cards/:id/invoices)
 * em faixa rolável, e a fatura selecionada com os itens (GET /credit-cards/invoices/:id), novo/editar/
 * excluir item, pagar/desfazer pagamento e atalho para importar a fatura.
 */
export const CardDrawer: React.FC<Props> = ({ cardId, initialInvoiceId, accounts, categories, onClose, onChanged, onError }) => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const canImport = checkPermission('financas.import').hasPermission;
  const [card, setCard] = useState<CreditCard | null>(null);
  const [range, setRange] = useState<{ from: Competence; to: Competence }>(() => {
    const now = currentCompetence();
    return { from: addCompetence(now, -3), to: addCompetence(now, 12) };
  });
  const [invoices, setInvoices] = useState<CreditCardInvoice[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [invoice, setInvoice] = useState<CreditCardInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newItem, setNewItem] = useState(false);
  const [editItem, setEditItem] = useState<CardTransaction | null>(null);
  const [deleting, setDeleting] = useState<CardTransaction | null>(null);
  const [paying, setPaying] = useState(false);
  const [unpaying, setUnpaying] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, list] = await Promise.all([creditCardService.getById(cardId), creditCardService.getInvoices(cardId, competenceToKey(range.from), competenceToKey(range.to))]);
      setCard(c);
      setInvoices(list);
      setSelectedId((cur) => {
        if (cur && list.some((i) => i.id === cur)) return cur;
        if (initialInvoiceId && list.some((i) => i.id === initialInvoiceId)) return initialInvoiceId;
        if (c.current_invoice && list.some((i) => i.id === c.current_invoice?.id)) return c.current_invoice.id;
        const unpaid = list.find((i) => i.status !== 'paid');
        return unpaid?.id ?? list[list.length - 1]?.id ?? null;
      });
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const loadInvoice = useCallback(async () => {
    if (!selectedId) {
      setInvoice(null);
      return;
    }
    setLoadingInvoice(true);
    try {
      setInvoice(await creditCardService.getInvoice(selectedId));
    } catch (err) {
      onError(err);
    } finally {
      setLoadingInvoice(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  /** Depois de qualquer alteração: fatura, lista de faturas, cartão e a tela de trás. */
  const changed = () => {
    load();
    loadInvoice();
    onChanged();
  };

  const confirmDelete = async (scope: DeleteScope) => {
    if (!deleting) return;
    setBusy(true);
    try {
      await creditCardService.removeTransaction(deleting.id, scope);
      setDeleting(null);
      changed();
    } catch (err) {
      setDeleting(null);
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const confirmUnpay = async () => {
    if (!invoice) return;
    setBusy(true);
    try {
      await creditCardService.unpay(invoice.id);
      setUnpaying(false);
      changed();
    } catch (err) {
      setUnpaying(false);
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const items = useMemo(() => invoice?.items ?? [], [invoice]);
  const isPaid = invoice?.status === 'paid';
  const openTotal = Math.abs(card?.open_total_cents ?? 0);
  const available = card?.limit_cents ? card.limit_cents - openTotal : null;
  const modalOpen = editing || newItem || !!editItem || !!deleting || paying || unpaying;
  const isInstallmentGroup = (t: CardTransaction) => !!t.transaction_group && !!t.repeat_total && t.repeat_total > 1;
  const canTouch = (t: CardTransaction) => canWrite && !isPaid && !isPaymentGeneratedItem(t);
  const touchHint = (t: CardTransaction) => (isPaid ? 'Fatura paga: desfaça o pagamento para alterar' : isPaymentGeneratedItem(t) ? 'Gerado pelo pagamento da fatura anterior: desfaça aquele pagamento' : undefined);

  const actions = (t: CardTransaction) =>
    canWrite ? (
      <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
        <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip={touchHint(t) ?? 'Editar'} disabled={!canTouch(t)} onClick={() => setEditItem(t)} />
        <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip={touchHint(t) ?? 'Excluir'} disabled={!canTouch(t)} onClick={() => setDeleting(t)} />
      </div>
    ) : null;

  return (
    <>
      <Drawer
        isOpen
        onClose={onClose}
        closeOnEsc={!modalOpen}
        size="xl"
        title={card ? <ColorChip color={card.color}>{card.name}</ColorChip> : 'Cartão'}
        headerActions={
          canWrite && card ? (
            <Button variant="outline" size="sm" icon={<Pencil />} onClick={() => setEditing(true)}>
              Editar
            </Button>
          ) : undefined
        }
      >
        {loading && !card ? (
          <p className="text-center text-slate-400 py-10">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        ) : card ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-small">
              {card.brand && <span>{CARD_BRAND_LABELS[card.brand]}</span>}
              {card.last4 && <span>final {card.last4}</span>}
              <span>
                fecha dia {card.closing_day} · vence dia {card.due_day}
              </span>
              {card.payment_account_name ? <span>paga por {card.payment_account_name}</span> : <span className="text-amber-700">sem conta de pagamento (sem espelho)</span>}
              {card.is_active === false && <span className="badge-neutral">Inativo</span>}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 card p-3">
              <Stat label="Limite">{card.limit_cents ? formatBRL(card.limit_cents) : <span className="text-slate-400">—</span>}</Stat>
              <Stat label="Em aberto" hint="Soma das faturas não pagas">
                <AmountText cents={-openTotal} />
              </Stat>
              <Stat label="Disponível" hint="Limite menos o que está em aberto">
                {available === null ? <span className="text-slate-400">—</span> : <BalanceText cents={available} />}
              </Stat>
            </div>

            {/* Faturas do intervalo */}
            <section className="space-y-2">
              <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-2">
                <span className="text-label">Faturas de</span>
                <CompetencePicker value={range.from} onChange={(c) => c && setRange((r) => ({ ...r, from: c }))} disabled={loading} />
                <span className="text-label">a</span>
                <CompetencePicker value={range.to} onChange={(c) => c && setRange((r) => ({ ...r, to: c }))} disabled={loading} />
              </div>
              {invoices.length === 0 ? (
                <p className="text-small py-2">Nenhuma fatura no intervalo.</p>
              ) : (
                <div className={clsx('flex gap-2 overflow-x-auto pb-1 custom-scrollbar', loading && 'opacity-60')}>
                  {invoices.map((inv) => (
                    <button
                      key={inv.id}
                      type="button"
                      onClick={() => setSelectedId(inv.id)}
                      className={clsx(
                        'min-w-[10rem] text-left rounded-lg border p-2.5 space-y-1 transition-colors flex-shrink-0',
                        selectedId === inv.id ? 'border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500' : 'border-slate-200 hover:bg-slate-50',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800">{inv.label}</span>
                        <InvoiceStatusBadge status={inv.status} />
                      </div>
                      <p className="text-sm">
                        <AmountText cents={inv.total_cents} muted={inv.total_cents === 0} />
                      </p>
                      <p className="text-[11px] text-slate-500">
                        {inv.status === 'paid' && inv.paid_at ? `paga em ${formatDateBR(inv.paid_at)}` : `vence ${formatDateBR(inv.due_date)}`}
                        {inv.items_count > 0 && ` · ${inv.items_count} ${inv.items_count === 1 ? 'item' : 'itens'}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Fatura selecionada */}
            {invoice && (
              <section className={clsx('space-y-3', loadingInvoice && 'opacity-60')}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-base font-semibold text-slate-800 flex flex-wrap items-center gap-2">
                    Fatura {invoice.label}
                    <InvoiceStatusBadge status={invoice.status} />
                  </h3>
                  <div className="text-sm text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>fecha {formatDateBR(invoice.closing_date)}</span>
                    <span>vence {formatDateBR(invoice.due_date)}</span>
                    <span>
                      total <AmountText cents={invoice.total_cents} className="font-semibold" />
                    </span>
                  </div>
                </div>
                {isPaid && (
                  <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    Paga em {formatDateBR(invoice.paid_at)} · {formatBRL(invoice.paid_amount_cents)}
                    {invoice.paid_amount_cents !== undefined && invoice.paid_amount_cents !== null && invoice.paid_amount_cents < Math.abs(invoice.total_cents)
                      ? ` (parcial: ${formatBRL(Math.abs(invoice.total_cents) - invoice.paid_amount_cents)} foi para a fatura seguinte)`
                      : ''}
                  </p>
                )}
                <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
                  {canWrite && (
                    <>
                      <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setNewItem(true)} disabled={isPaid} title={isPaid ? 'Fatura paga: lance com uma data de outra fatura' : undefined}>
                        Novo item
                      </Button>
                      {isPaid ? (
                        <Button variant="outline" size="sm" icon={<RotateCcw />} onClick={() => setUnpaying(true)}>
                          Desfazer pagamento
                        </Button>
                      ) : (
                        <Button variant="success" size="sm" icon={<BadgeCheck />} onClick={() => setPaying(true)} disabled={invoice.total_cents === 0}>
                          Pagar fatura
                        </Button>
                      )}
                    </>
                  )}
                  {canImport && (
                    <Link to={`/finance/import?card_id=${card.id}`} className="btn-outline text-sm px-3 py-1.5 inline-flex items-center gap-1.5 justify-center">
                      <Upload size={16} /> Importar fatura
                    </Link>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  {items.length === 0 ? (
                    <p className="text-center text-slate-500 py-8 text-sm">Nenhum item nesta fatura.</p>
                  ) : (
                    <>
                      {/* Celular: cartões */}
                      <ul className="md:hidden divide-y divide-slate-100">
                        {items.map((t) => (
                          <li key={t.id} className={clsx('px-3 py-2 space-y-1', canTouch(t) && 'cursor-pointer hover:bg-slate-50')} onClick={() => canTouch(t) && setEditItem(t)}>
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm text-slate-800 [overflow-wrap:anywhere] min-w-0">
                                {t.description}
                                {itemBadges(t)}
                              </p>
                              <AmountText cents={t.amount_cents} className="text-sm font-medium" />
                            </div>
                            <div className="flex items-center justify-between gap-2 text-small">
                              <span>
                                {formatDateBR(t.purchase_date)}
                                {t.category_name ? ` · ${t.category_name}` : ''}
                                {t.original_currency && t.original_amount_cents ? ` · ${t.original_currency} ${formatBRL(t.original_amount_cents).replace('R$ ', '')}` : ''}
                              </span>
                              {actions(t)}
                            </div>
                          </li>
                        ))}
                      </ul>
                      {/* Tabela */}
                      <table className="hidden md:table w-full text-sm">
                        <thead className="table-header">
                          <tr>
                            <th className="py-2 px-3 text-left text-xs font-semibold text-slate-600 uppercase w-28">Data</th>
                            <th className="py-2 px-3 text-left text-xs font-semibold text-slate-600 uppercase">Descrição</th>
                            <th className="py-2 px-3 text-left text-xs font-semibold text-slate-600 uppercase">Categoria</th>
                            <th className="py-2 px-3 text-right text-xs font-semibold text-slate-600 uppercase">Valor</th>
                            <th className="w-24"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((t) => (
                            <tr key={t.id} className={clsx('border-t border-slate-100 hover:bg-slate-50', canTouch(t) && 'cursor-pointer')} onClick={() => canTouch(t) && setEditItem(t)}>
                              <td className="py-2 px-3 whitespace-nowrap text-slate-600">{formatDateBR(t.purchase_date)}</td>
                              <td className="py-2 px-3 [overflow-wrap:anywhere]">
                                {t.description}
                                {itemBadges(t)}
                                {t.original_currency && t.original_amount_cents ? (
                                  <span className="block text-[11px] text-slate-400">
                                    {t.original_currency} {formatBRL(t.original_amount_cents).replace('R$ ', '')}
                                  </span>
                                ) : null}
                              </td>
                              <td className="py-2 px-3 text-slate-600">{t.category_name ? <ColorChip color={t.category_color}>{t.category_name}</ColorChip> : <span className="text-amber-700 text-xs">sem categoria</span>}</td>
                              <td className="py-2 px-3 text-right">
                                <AmountText cents={t.amount_cents} className="font-medium" />
                              </td>
                              <td className="py-1 px-2">{actions(t)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 text-sm flex flex-wrap items-center justify-between gap-x-2">
                    <span className="text-slate-600">Total da fatura</span>
                    <AmountText cents={invoice.total_cents} className="font-semibold" />
                  </div>
                </div>
              </section>
            )}
          </div>
        ) : null}
      </Drawer>

      {editing && card && (
        <CardFormModal
          card={card}
          accounts={accounts}
          categories={categories}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            changed();
          }}
          onError={onError}
        />
      )}

      {(newItem || editItem) && card && (
        <CardTransactionFormModal
          card={card}
          transaction={editItem}
          categories={categories}
          prefillDate={!editItem && invoice ? dateInsideInvoice(card.closing_day, invoice) : undefined}
          onClose={() => {
            setNewItem(false);
            setEditItem(null);
          }}
          onSaved={() => {
            setNewItem(false);
            setEditItem(null);
            changed();
          }}
          onError={onError}
        />
      )}

      {paying && invoice && (
        <PayInvoiceModal
          invoice={invoice}
          onClose={() => setPaying(false)}
          onSaved={() => {
            setPaying(false);
            changed();
          }}
          onError={onError}
        />
      )}

      <ConfirmDialog
        isOpen={unpaying}
        onClose={() => setUnpaying(false)}
        onConfirm={confirmUnpay}
        title="Desfazer pagamento"
        message={`Desfazer o pagamento da fatura ${invoice?.label ?? ''}? Os itens "Saldo anterior" e "Juros e encargos" gerados na fatura seguinte são removidos e o espelho na conta volta a ser previsto no vencimento.`}
        confirmText="Desfazer"
        severity="warning"
        loading={busy}
      />

      {/* Exclusão: parcela pergunta o escopo; os demais confirmam direto. */}
      {deleting && isInstallmentGroup(deleting) ? (
        <Modal isOpen onClose={() => !busy && setDeleting(null)} size="sm" closeOnBackdrop={!busy} showCloseButton={!busy}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-h3">Excluir parcela</h2>
          </div>
          <div className="px-6 py-4 space-y-2 text-sm text-slate-700">
            <p>
              "{deleting.description}" é a parcela <strong>{deleting.repeat_index}/{deleting.repeat_total}</strong>. O que excluir?
            </p>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg grid grid-cols-1 sm:flex sm:justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => confirmDelete('one')} loading={busy}>
              Só esta
            </Button>
            <Button variant="danger" onClick={() => confirmDelete('future')} loading={busy}>
              Esta e as seguintes
            </Button>
          </div>
        </Modal>
      ) : (
        <ConfirmDialog
          isOpen={!!deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() => confirmDelete('one')}
          title="Excluir item"
          message={`Excluir "${deleting?.description ?? ''}" de ${deleting ? formatDateBR(deleting.purchase_date) : ''} (${deleting ? formatBRL(deleting.amount_cents) : ''})?`}
          confirmText="Excluir"
          severity="danger"
          loading={busy}
        />
      )}
    </>
  );
};
